<template>
    <nio-modal
        name="template"
        size="md"
        :title="$t('chat.messageTemplate.template')"
        ref="modal"
        @closed="onClosed"
        @open="onOpen"
    >
        <section class="space-y-6">
            <div class="flex space-x-6">
                <nio-input-group class="flex-1" :label="$t('chat.messageTemplate.category')">
                    <nio-select :options="templateCategoryOptions" v-model="newCategory" />
                </nio-input-group>
                <nio-input-group class="flex-1" :label="$t('chat.messageTemplate.name')">
                    <nio-input v-model="newName" />
                </nio-input-group>
            </div>

            <!-- TODO: Merge w/ MessageBuilder.vue -->
            <nio-input-group :label="$t('automations.modal.subject')">
                <template #after-label>
                    <language-chooser class="link-gray | ml-2" :locales="locales" v-model="activeLocale" />
                </template>
                <template #right-label>
                    <attributes-dropdown @insert="insertAttributeInSubject" />
                </template>
                <nio-input :placeholder="$t('chat.messageTemplate.subject')" ref="subject" v-model="subjects[activeLocale]" />
            </nio-input-group>

            <nio-input-group :label="$t('automations.modal.message')">
                <template #after-label>
                    <language-chooser class="link-gray | ml-2" :locales="locales" v-model="activeLocale" />
                </template>
                <template #right-label>
                    <attributes-dropdown @insert="insertAttributeInMessage" />
                </template>
                <nio-textarea
                    :placeholder="$t('chat.messageInput.enterMessage')"
                    rows="3"
                    ref="message"
                    v-model="messages[activeLocale]"
                />
            </nio-input-group>

            <hr>

            <nio-input-group :label="$t('appointments.sendConfirmationModal.preview')">
                <div class="relative min-h-24 px-4 py-2 border border-gray-200 rounded bg-white">
                    <p v-html="messagePreview" />
                    <message-segments-usage
                        class="absolute right-0 bottom-0 flex rounded-tl rounded-br"
                        :message="messagePreview"
                    />
                </div>
            </nio-input-group>
        </section>

        <template #footer>
            <nio-button @click="close">
                {{ $t('chat.messageTemplate.close') }}
            </nio-button>

            <nio-button
                color="blue"
                :disabled="!valid"
                @click="onSave"
            >
                {{ $t('chat.messageTemplate.save') }}
            </nio-button>
        </template>
    </nio-modal>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import { nl2br } from '@/utils/index.js';
    import MessageParsing from '@/mixins/MessageParsing.js';

    import AttributesDropdown from '@/components/AttributesDropdown.vue';
    import LanguageChooser from '@/components/LanguageChooser.vue';
    import MessageSegmentsUsage from '@/components/MessageSegmentsUsage.vue';

    export default {
        components: {
            AttributesDropdown,
            LanguageChooser,
            MessageSegmentsUsage,
        },

        mixins: [MessageParsing],

        data() {
            return {
                activeLocale: 'fr',
                newName: '',
                subjects: {
                    fr: '',
                    en: '',
                },
                messages: {
                    fr: '',
                    en: '',
                },
                newCategory: null,
            };
        },

        computed: {
            ...mapState('chat', [
                'selectedTemplate',
                'templateCategories',
            ]),

            valid() {
                if (this.newName != null) {
                    if (!this.newCategory ||
                        empty(this.newName) ||
                        (
                            empty(this.messages.fr) &&
                            empty(this.messages.en)
                        )
                    ) {
                        return false;
                    }
                }

                return true;
            },

            messagePreview() {
                return nl2br(this.fillTemplateValues(this.messages[this.activeLocale], this.exampleAppointment, null, false));
            },

            exampleAppointment() {
                return {
                    datetime: moment().format(),
                    contact: {
                        civility: 'mr',
                        first_name: 'John',
                        last_name: 'Doe',
                        locale: this.activeLocale,
                        placeholders: [
                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:year',
                                value: '2021',
                            },
                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:make',
                                value: 'Tesla',
                            },
                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:model',
                                value: 'Model 3',
                            },
                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:trim',
                                value: 'Standard Range',
                            },

                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:stock',
                                value: 'A12345',
                            },
                            {
                                campaign_id: null,
                                original_name: 'wantedVehicle:vin',
                                value: 'JHMSZ542XDC028494',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:year',
                                value: '2010',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:make',
                                value: 'Toyota',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:model',
                                value: 'Camry',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:trim',
                                value: 'SE',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:stock',
                                value: 'B09876',
                            },
                            {
                                campaign_id: null,
                                original_name: 'exchangeVehicle:vin',
                                value: 'WAUMGAFL1DA105812',
                            },
                        ],
                    },
                    contact_phone: {
                        number: '14501231234',
                    },
                    amount: 9999,
                    invoice_number: 'INV001',
                };
            },

            templateCategoryOptions() {
                return this.templateCategories.map(category => {
                    return {
                        value: category.id,
                        label: category.name,
                    };
                });
            },

            locales() {
                return [
                    { value: 'fr', label: 'Français' },
                    { value: 'en', label: 'English' },
                ];
            },
        },

        methods: {
            ...mapActions('chat', [
                'fetchTemplateCategories',
            ]),

            init() {
                if (!this.selectedTemplate) {
                    return;
                }

                this.newName = this.selectedTemplate.name;
                this.newCategory = this.selectedTemplate.template_category_id;
                this.subjects.fr = this.selectedTemplate.subject.fr;
                this.subjects.en = this.selectedTemplate.subject.en;
                this.messages.fr = this.selectedTemplate.content.fr;
                this.messages.en = this.selectedTemplate.content.en;
            },

            reset() {
                this.newName = '';
                this.newCategory = null;
                this.subjects.fr = '';
                this.subjects.en = '';
                this.messages.fr = '';
                this.messages.en = '';
            },

            async onSave() {
                this.messages = this.duplicateTranslationIfEmpty(this.messages);
                this.subjects = this.duplicateTranslationIfEmpty(this.subjects);

                const payload = {
                    subject: this.subjects,
                    content: this.messages,
                    name: this.newName,
                    template_category_id: this.newCategory,
                };

                if (this.selectedTemplate.id != -1) {
                    this.update(payload);
                } else {
                    this.create(payload);
                }
            },

            async create(payload) {
                try {
                    await this.$axios.post('templates', payload);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('chat.messageTemplateModal.successes.created'),
                    });

                    this.fetchTemplateCategories();
                    this.close();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.messageTemplateModal.errors.creating'),
                    });

                    throw error;
                }
            },

            async update(payload) {
                try {
                    await this.$axios.put(`templates/${this.selectedTemplate.id}`, payload);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('chat.messageTemplateModal.successes.updated'),
                    });

                    this.fetchTemplateCategories();
                    this.close();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.messageTemplateModal.errors.updating'),
                    });

                    throw error;
                }
            },

            insertAttributeInSubject(attribute) {
                this.$refs.subject.insertAtCaret(attribute);
            },

            insertAttributeInMessage(attribute) {
                this.$refs.message.insertAtCaret(attribute);
            },

            close() {
                this.$refs.modal.close();
            },

            onOpen() {
                this.init();
            },

            onClosed() {
                this.reset();
            },
        },
    };
</script>
